<script setup>
import Loading from '../../Loading.vue';
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import { useCreateOnArrival } from '../../../composables/useCreateOnArrival';
import CharacterCounter from '../../communications/CharacterCounter.vue';
import CommsEditorPage from '../../communications/CommsEditorPage.vue';
import XlButton from '../../xl/XlButton.vue';
import CommsInfoBanner from '../../communications/CommsInfoBanner.vue';
import { COMMS_HINTS } from './hints';
import { plural } from '../../../helpers/plural';
import { TERM_TIPS } from '../../../helpers/termTips';
import XlCard from '../../xl/XlCard.vue';
import XlTag from '../../xl/XlTag.vue';
import XlTooltip from '../../xl/XlTooltip.vue';
import XlStatusIcon from '../../xl/XlStatusIcon.vue';
import XlListRow from '../../xl/XlListRow.vue';
import SegmentAudiencePanel from '../../communications/SegmentAudiencePanel.vue';
import SegmentRuleBuilder from '../../communications/SegmentRuleBuilder.vue';
import { summarizeRule } from '../../communications/segmentRuleHelpers';
import { useSelection } from '../../communications/useSelection';
import { createSegment, deleteSegment, estimateSegment, listSegments, updateSegment } from '../../../api/segments';
import { useCommunicationsStore } from '../../../stores/communications';

import XlField from '../../xl/XlField.vue';


const NAME_MAX = 60;
const DEFAULT_RULE = { definition_type: 'policy_type', policy_type: 'auto' };

const store = useCommunicationsStore();
const { selectedIds, isSelected, toggle, clear, selectedCount } = useSelection();

import { useAuthStore } from '../../../stores/auth';
import { canAssignBrokers } from '../../../helpers/roles';

const auth = useAuthStore();
// The agency owns the definitions. A broker uses them and cannot make one —
// forty overlapping segments in a quarter is the outcome otherwise.
const canDefineSegments = computed(() => canAssignBrokers(auth.userData));

const segments = ref([]);
const loadingList = ref(true);
const listError = ref('');
// segment id -> { policies, policyholders } | 'loading' | 'error'
const estimates = reactive({});

const isFormOpen = ref(false);
const editingId = ref(null);
const formName = ref('');
const formRule = ref(DEFAULT_RULE);
const formRuleValid = ref(true);
// Mirrored out of the rule builder so the panel beside it can show who the
// rule reaches without estimating the same thing a second time.
const formEstimate = ref(null);
const saving = ref(false);
const saveError = ref('');
const nameCounter = ref(null);
const nameField = ref(null);

const deleteWarning = reactive({ segmentId: null, segmentName: '', sequences: [], items: 0, recordings: 0 });
const recordingsNote = computed(() => {
  const n = deleteWarning.recordings;
  if (!n) return '';
  return deleteWarning.items === 1 ? ', a recording' : `, ${n} of them ${n === 1 ? 'a recording' : 'recordings'}`;
});

const nameBlank = computed(() => formName.value.trim().length === 0);
const nameOverLimit = computed(() => nameCounter.value?.isOver ?? false);
const canSave = computed(() => !nameBlank.value && !nameOverLimit.value && formRuleValid.value && !saving.value);

const count = (value) => Number(value ?? 0).toLocaleString('en-US');

/**
 * The hub's number, or for a broker her own number and then the agency's, so
 * a segment of 412 that reaches 51 of hers does not read as a segment of 51.
 */
const countTags = (id) => {
  const estimate = estimates[id];
  if (!estimate || estimate === 'loading' || estimate === 'error') return [];

  if (estimate.mine === null || estimate.mine === undefined) {
    return [{ testid: 'segment-count', words: plural(estimate.policyholders, 'policyholder') }];
  }

  return [
    { testid: 'segment-count', n: count(estimate.mine), words: 'of yours' },
    { testid: 'segment-agency-count', n: count(estimate.policyholders), words: 'agency-wide', agency: true },
  ];
};

const refreshEstimates = () => {
  segments.value.forEach((segment) => {
    estimates[segment.id] = 'loading';
    estimateSegment(segment.rule)
      .then((data) => {
        estimates[segment.id] = data;
      })
      .catch(() => {
        estimates[segment.id] = 'error';
      });
  });
};

const fetchList = async () => {
  loadingList.value = true;
  listError.value = '';
  try {
    segments.value = await listSegments();
    refreshEstimates();
  } catch (e) {
    listError.value = 'Could not load segments.';
  } finally {
    loadingList.value = false;
  }
};

// Set only when the Overview's "Build a segment" tile opened this, so the
// cursor lands in the name field rather than on the Back button above it.
const focusName = ref(false);

const { cancelReturn: returnFromTile } = useCreateOnArrival('segment', () => {
  openNew();
  focusName.value = true;
});

// Arriving from the Overview tile puts the cursor in the name field rather than
// on the Back button above it. Asked of the field once the form has rendered,
// which is why it waits a tick.
watch(isFormOpen, async (open) => {
  if (!open || !focusName.value) return;
  await nextTick();
  nameField.value?.focus();
});

// A broker can still arrive on ?create=segment, though nothing offers her the button.
const openNew = () => {
  if (!canDefineSegments.value) return;
  clear();
  editingId.value = null;
  formName.value = '';
  formRule.value = DEFAULT_RULE;
  formRuleValid.value = true;
  formEstimate.value = null;
  saveError.value = '';
  isFormOpen.value = true;
};

const openEdit = (segment) => {
  clear();
  editingId.value = segment.id;
  formName.value = segment.name;
  formRule.value = segment.rule;
  formRuleValid.value = true;
  formEstimate.value = null;
  saveError.value = '';
  isFormOpen.value = true;
};

const closeForm = () => {
  isFormOpen.value = false;
  focusName.value = false;
  // Backing out of a form a tile opened returns you where the tile was.
  returnFromTile();
};

const handleRuleUpdate = (rule) => {
  formRule.value = rule;
};

const handleRuleValid = (valid) => {
  formRuleValid.value = valid;
};

const handleEstimateChanged = (estimate) => {
  formEstimate.value = estimate;
};

const save = async () => {
  if (!canSave.value) return;
  saving.value = true;
  saveError.value = '';
  try {
    const payload = {
      name: formName.value,
      definition_type: formRule.value.definition_type,
      rule: formRule.value,
    };
    if (editingId.value) {
      await updateSegment(editingId.value, payload);
    } else {
      await createSegment(payload);
    }
    isFormOpen.value = false;
    // Reset here as well as in closeForm: leaving it set meant every later
    // "Edit segment" yanked focus into the name field, which is exactly what
    // the tile flow is careful not to do.
    focusName.value = false;

    // Saving a segment was silent. A segment is a definition of an audience, so
    // its reach is the one number worth confirming — and this tab already
    // fetches it. Falls back to naming the segment when the estimate is not in.
    const reach = await estimateSegment(formRule.value)
      .then((data) => data?.policyholders ?? null)
      .catch(() => null);

    window.loadNotification({
      message: reach === null
        ? `Segment "${formName.value}" saved`
        : `Segment saved — ${reach.toLocaleString('en-US')} ${reach === 1 ? 'policyholder' : 'policyholders'}`,
    });

    await fetchList();
  } catch (e) {
    saveError.value = 'Could not save this segment. Please try again.';
  } finally {
    saving.value = false;
  }
};

const requestDelete = async (segment, { confirm = false } = {}) => {
  try {
    const result = await deleteSegment(segment.id, { confirm });
    const referencing = result?.referencing_sequences || [];
    if (referencing.length > 0) {
      Object.assign(deleteWarning, { segmentId: segment.id, segmentName: segment.name, sequences: referencing, items: 0, recordings: 0 });
      return;
    }
    // Files, banners and links aimed at the segment reach nobody once it is
    // gone; say how many before the second, confirmed request.
    if (result?.referencing_items > 0 && !confirm) {
      Object.assign(deleteWarning, {
        segmentId: segment.id, segmentName: segment.name, sequences: [], items: result.referencing_items, recordings: result.referencing_recordings,
      });
      return;
    }
    segments.value = segments.value.filter((s) => s.id !== segment.id);
    delete estimates[segment.id];
    dismissDeleteWarning();
  } catch (e) {
    listError.value = 'Could not delete this segment.';
  }
};

const confirmDelete = () => {
  const segment = segments.value.find((s) => s.id === deleteWarning.segmentId);
  if (segment) requestDelete(segment, { confirm: true });
};

const deleteSelected = async () => {
  for (const id of [...selectedIds.value]) {
    await requestDelete(segments.value.find((segment) => segment.id === id));
  }
  clear();
};

watch(selectedCount, (count) => {
  store.setToolbar({ deleteDisabled: count === 0, onDelete: deleteSelected });
}, { immediate: true });

const dismissDeleteWarning = () => {
  Object.assign(deleteWarning, { segmentId: null, segmentName: '', sequences: [], items: 0, recordings: 0 });
};

onMounted(fetchList);
// The tabs above belong to the parent, so it is told to stand down while
// this editor owns the screen.
watch(isFormOpen, (open) => store.setEditing(open));

onBeforeUnmount(() => {
  store.clearToolbar();
  store.setEditing(false);
});
</script>

<template>
  <div class="segments-tab">
    <div v-if="!isFormOpen">
      <CommsInfoBanner :text="COMMS_HINTS.segments">
        <template v-if="canDefineSegments">
          <slot name="actions" />
          <XlButton data-testid="new-segment" @click="openNew">
            <template #icon>
              <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <path d="M10 4.5v11M4.5 10h11" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
              </svg>
            </template>
            New segment
          </XlButton>
        </template>
      </CommsInfoBanner>

      <XlCard v-if="!canDefineSegments" padded class="segments-tab__note" data-testid="segments-agency-note">
        <span class="segments-tab__note-ico" aria-hidden="true">
          <svg width="23" height="23" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="8" /><path d="M12 11.4v4.8M12 8.2h.01" /></svg>
        </span>
        <div>
          Segments are set by the agency, so everyone works from the same definitions.
          The counts below are <b>your</b> policyholders inside each one.
        </div>
      </XlCard>

      <p v-if="listError" class="segments-tab__status segments-tab__status--error" role="alert">{{ listError }}</p>
      <Loading v-else-if="loadingList" variant="rows" :rows="4" data-testid="segments-loading" />
      <p v-else-if="!segments.length" class="segments-tab__status">
        No segments yet. Create one to start targeting policyholders.
      </p>

      <XlCard v-else class="segments-tab__list">
        <XlListRow
          v-for="segment in segments"
          :key="segment.id"
          data-testid="segment-row"
          :class="{ 'segments-tab__row--selected': isSelected(segment.id) }"
        >
          <template #leading>
            <XlField
              v-if="canDefineSegments"
              type="checkbox"
              :name="`segment-${segment.id}`"
              :aria-label="`Select ${segment.name}`"
              data-testid="segment-row-checkbox"
              :model-value="isSelected(segment.id)"
              @update:model-value="(checked) => toggle(segment.id, checked)"
            />
            <XlStatusIcon tone="brand"><svg width="23" height="23" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="9" cy="8" r="3.2" /><path d="M3.6 20v-1.4A4 4 0 0 1 7.6 14.6h2.8a4 4 0 0 1 4 4V20" /><circle cx="17.4" cy="9" r="2.4" /><path d="M16 14.7h1.8a3 3 0 0 1 3 3V20" /></svg></XlStatusIcon>
          </template>

          <template #title>{{ segment.name }}</template>
          <template #subtitle>{{ summarizeRule(segment.rule) }}</template>

          <template #trailing>
            <XlTag
              v-for="tag in countTags(segment.id)"
              :key="tag.testid"
              :tone="tag.agency ? undefined : 'brand'"
              :class="{ 'segments-tab__agency': tag.agency }"
              :data-testid="tag.testid"
            >
              {{ tag.n }}
              <XlTooltip v-if="TERM_TIPS[tag.words]" :text="TERM_TIPS[tag.words]" underline>{{ tag.words }}</XlTooltip>
              <template v-else>{{ tag.words }}</template>
            </XlTag>

            <XlButton
              v-if="canDefineSegments"
              data-testid="segment-row-edit"
              variant="ghost"
              size="sm"
              @click="openEdit(segment)"
            >
              Edit
            </XlButton>
          </template>
        </XlListRow>
      </XlCard>

      <div
        v-if="deleteWarning.segmentId"
        class="segments-tab__delete-warning"
        role="alert"
        data-testid="delete-warning"
      >
        <template v-if="deleteWarning.sequences.length">
          <p>This segment can't be deleted — it's still used by {{ deleteWarning.sequences.join(', ') }}.</p>
          <XlButton variant="ghost" @click="dismissDeleteWarning">OK</XlButton>
        </template>
        <template v-else>
          <p>{{ deleteWarning.items }} {{ deleteWarning.items === 1 ? 'item is' : 'items are' }} aimed at this segment{{ recordingsNote }}. Until somebody re-aims them they will reach nobody.</p>
          <div class="segments-tab__delete-actions">
            <XlButton variant="ghost" @click="dismissDeleteWarning">Keep it</XlButton>
            <XlButton variant="danger" @click="confirmDelete">Delete anyway</XlButton>
          </div>
        </template>
      </div>
    </div>

    <CommsEditorPage
      v-else
      testid="segment-editor"
      :changes="{ name: formName, rule: formRule }"
      back-label="Back to segments"
      :title="editingId ? 'Edit segment' : 'New segment'"
      save-label="Save segment"
      :save-disabled="!canSave || saving"
      @back="closeForm"
      @cancel="closeForm"
      @save="save"
    >
      <div>
        <div class="segments-tab__fld">
          <XlField
            ref="nameField"
            v-model="formName"
            name="segment_name"
            data-testid="segment-name-input"
            label="Segment name"
            :tip="TERM_TIPS.Segment"
            :maxlength="NAME_MAX"
            :counted="false"
            placeholder="e.g. Chicago home renewals"
          />
          <CharacterCounter ref="nameCounter" :value="formName" :max="NAME_MAX" field="input" plain-text />
        </div>

        <SegmentRuleBuilder
          :model-value="formRule"
          @update:model-value="handleRuleUpdate"
          @update:valid="handleRuleValid"
          @estimate-changed="handleEstimateChanged"
        />

        <p v-if="saveError" class="segments-tab__status segments-tab__status--error" role="alert" data-testid="save-error">{{ saveError }}</p>
      </div>

      <template #aside>
        <SegmentAudiencePanel :rule="formRule" :estimate="formEstimate" :valid="formRuleValid" />
      </template>
    </CommsEditorPage>
  </div>
</template>

<style scoped lang="scss">
.segments-tab {
  &__note {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;

    > div { font-size: 13.5px; color: var(--xl-color-secondary-text); }
  }

  &__note-ico {
    display: grid;
    flex: 0 0 42px;
    place-items: center;
    width: 42px;
    height: 42px;
    border-radius: 13px;
    color: var(--xl-color-primary-text);

    svg { display: block; }
  }

  // Clipped, so a selected row's fill stops at the card's rounded corner.
  &__list { padding: 8px 22px; overflow: hidden; }

  // box-shadow carries the fill across the card's 22px side padding.
  &__row--selected {
    background: var(--xl-color-primary-container);
    box-shadow: -22px 0 0 var(--xl-color-primary-container), 22px 0 0 var(--xl-color-primary-container);

    // The brand tag's and icon tile's own fill is this same tint, so they would vanish here.
    .xl-tag--brand,
    .xl-status-icon { background: #fff; }
  }

  &__agency { color: var(--xl-color-outline); }

  &__status {
    margin: 0 0 14px;
    font-size: 13.5px;
    color: var(--xl-color-outline);

    &--error { color: var(--xl-color-error); }
  }

  &__fld { margin-bottom: 16px; }

  &__delete-warning {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 11px 14px;
    border-radius: 12px;
    background: var(--xl-color-error-container);
    color: var(--xl-color-error);
    font-size: 13px;
    line-height: 1.55;

    p { margin: 0; }
  }

  &__delete-actions {
    display: flex;
    flex: 0 0 auto;
    gap: 8px;
  }
}
</style>
